import cn from 'classnames'
import Link from 'next/link'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import TooltipModel from './TooltipModel'

interface InsightCardTagsProps {
  //key?: string
  cardId: string
  className?: string
  tagItems?: string | null
  contentTagsLink?: any[] | null
  openTooltipCardId?: string | null
  setOpenTooltipCardId: (id: string | null) => void
  containerSize?: number
  subjectMatterPopup?: number
  setHideShowTooltip?: (hide: boolean) => void
  onWhichPage?: string
  isMobile?: boolean
}

export const InsightCardTags: React.FC<InsightCardTagsProps> = ({
  //key,
  cardId,
  tagItems,
  contentTagsLink,
  openTooltipCardId,
  setOpenTooltipCardId,
  containerSize = 292,
  subjectMatterPopup,
  setHideShowTooltip,
  onWhichPage = '',
  isMobile = false,
}) => {
  // eslint-disable-next-line no-console
  /* let tagArray = tagItems?.split(',').map((item: string) => item.trim()) || []
  if (tagArray && tagArray.length > 0) {
    tagArray = tagArray?.sort((a: string, b: string) => a.length - b.length)
  } */

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tags, setTags] = useState<string[]>([])
  const [showTag, setShowTag] = useState<boolean>(false)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openCardId, setOpenCardID] = useState(cardId)
  const [isOpen, setIsOpen] = useState(false)

  // Add refs for the button and tooltip container
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const containerWidth = containerSize //292 // Fixed width of the container in px
  const characterWidth = 8.5 // Average width of one character in px
  const badgePadding = 10 // Total horizontal padding (px-2 = 8px * 2)
  const badgeSpacing = onWhichPage === 'content-list-item' ? 16 : 8 // 8 // No extra spacing assumed between badges

  // Function to calculate how many badges fit dynamically
  const badgesToDisplay = useMemo(() => {
    let availableWidth = containerWidth
    const displayedBadges: string[] = []

    const needsMoreButton = tags.length > 1 // or you could be stricter if needed
    const moreButtonWidth = 67 //10 // Estimated width of "+n more" button

    if (needsMoreButton) {
      availableWidth -= moreButtonWidth
    }

    for (const tag of tags) {
      const textWidth = tag.length * characterWidth
      const badgeWidth = textWidth + badgePadding + badgeSpacing

      if (availableWidth >= badgeWidth) {
        displayedBadges.push(tag)
        availableWidth -= badgeWidth
      } else {
        break
      }
    }

    return displayedBadges
  }, [tags])

  useEffect(() => {
    if (tagItems && tagItems !== null) {
      const tagArray =
        tagItems
          ?.split(',')
          ?.map((item) => item.trim())
          .filter((item) => item && item.trim() !== '') || []
      // Sort insights tags base on the length of the tag
      /* if (onWhichPage === 'content-list-item') {
        tagArray = tagArray.sort((a, b) => a.length - b.length)
      } */
      setTags(tagArray)
    }
  }, [tagItems])

  const [currentTooltipCardId, setCurrentTooltipCardId] = useState<string | null | undefined>(cardId)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [buttonId, setButtonId] = useState<string | null | undefined>(cardId)

  const handleTooltipToggle = (currentTooltipCardId: string) => {
    setCurrentTooltipCardId(currentTooltipCardId)

    if (openTooltipCardId === cardId) {
      setOpenTooltipCardId(null) // Close the tooltip if already open
      setHideShowTooltip?.(false)
    } else {
      setOpenTooltipCardId?.(cardId) // Open the current tooltip
      setHideShowTooltip?.(true)
    }
    setShowTag(!showTag)
    setIsOpen(!isOpen)
  }
  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if tooltip is open for this card
      if (openTooltipCardId === cardId) {
        // const target = event.target as Node
        const targetElement = event.target as HTMLElement
        // Check if click is outside both the button and tooltip
        // const isOutsideButton = buttonRef.current && !buttonRef.current.contains(target)
        // const isOutsideTooltip = tooltipRef.current && !tooltipRef.current.contains(target)

        if (!targetElement.hasAttribute('id') && buttonRef.current?.id === currentTooltipCardId?.toString()) {
          setOpenTooltipCardId(null)
          setHideShowTooltip?.(false)
          setShowTag(false)
          setIsOpen(false)
        }
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openTooltipCardId, cardId, setOpenTooltipCardId, setHideShowTooltip])

  const getLink = (linkName: string) => {
    const link = contentTagsLink?.find((item) => item.name === linkName)?.link ?? ''
    return '/' + link
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getTagBGColour = (linkName: string) => {
    // if (linkName.includes('know-how/')) {
    //   return 'bg-gray-200'
    // }
    // if (linkName.includes('legaltech-resources-for-lawyers/')) {
    //   return 'bg-green-200'
    // }
    // if (linkName.includes('resources-for-solution-providers/')) {
    //   return 'bg-yellow-200'
    // }
    return 'bg-gray-200' // default
  }
  interface ListBadgesProps {
    tags?: string[]
    buttonId?: string
  }
  const ListBadges: React.FC<ListBadgesProps> = ({ tags, buttonId = '' }) => {
    if (tags && tags.length > 1 && tags.length > badgesToDisplay.length) {
      return (
        <button
          key={buttonId + 'more-btn'}
          id={buttonId + 'more-btn'}
          ref={buttonRef}
          className={cn(
            'px-1 pt-[3px] pb-[3px] text-blue-600 text-[12px] font-medium rounded relative z-[9999]',
            openTooltipCardId === cardId && 'tooltip-open',
          )}
          onClick={() => handleTooltipToggle(buttonId || '')}
        >
          {openTooltipCardId !== cardId ? `+${tags.length - badgesToDisplay.length} more` : '- less'}
        </button>
      )
    }
    return ''
  }

  useEffect(() => {
    //setOpenTooltipCardId(null)
    setOpenCardID('0')
  }, [subjectMatterPopup])

  return (
    <div
      className={`badges_wrapper d-inline-block relative ${isMobile ? 'flex w-full items-center' : ''}  ${showTag ? 'show' : ''} h-[22px]`}
    >
      {badgesToDisplay.map((item, index) => (
        <Link
          key={`${cardId}-tag-${item}-${index}`}
          id={`${cardId}-tag-${index}`}
          title={item}
          href={getLink(item)}
          className={cn(
            'badges px-2 py-1 rounded-full font-semibold',
            getTagBGColour(getLink(item)),
            onWhichPage === 'content-list-item' && '!mr-4',
          )}
        >
          {item}
        </Link>
      ))}

      <div
        key={cardId + 'more-btn-container'}
        id={cardId + 'more-btn-container'}
        className={`more_btn relatidve  ${isMobile ? 'flex-1' : ''} `}
      >
        {openTooltipCardId === cardId && (
          <div ref={tooltipRef}>
            <TooltipModel
              tags={tags.filter((item) => !badgesToDisplay.includes(item))}
              contentTagsLink={contentTagsLink}
            />
          </div>
        )}
        {openCardId && <ListBadges tags={tags} buttonId={buttonId || ''} />}
      </div>
    </div>
  )
}
